import {
  Grid,
  Table as MTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import { updatedDiff } from 'deep-object-diff'
import { Auto, CheckBox, Text } from './controls'
import { dateEquals, utcDateString } from './date-time'
import { Audit, ChangeReason, NdcInformation, Procedure } from './domain'
import { Control, FormApi, formApi } from './form'
import { LabelItem, splitCamelCase, Stringify, stringifyProcedure, withTooltip } from './format'
import { gray, orange } from './theme'
import { VisitCode } from './visit-context'
import React from 'react'

// TODO: support nested
export function diffReasons<T extends object = object>(a: T, b: T, keys?: Array<keyof T>): ChangeReason[] {
  const include = keys ? (a: keyof T) => keys.indexOf(a) > -1 : () => true
  const result: object = updatedDiff(a, b)
  let out: ChangeReason[] = []

  console.info('original diff objects', a, b)

  Object.keys(result).forEach((key) => {
    if (include(key as any)) {
      // serviceDate is a calendar date, not a point in time: keep it as a date-only
      // string so it isn't shifted by timezone when serialized (e.g. to JSON) downstream.
      const isServiceDate = key === 'serviceDate'
      const reason: ChangeReason = {
        fieldName: key,
        oldValue: isServiceDate ? (utcDateString(a[key] as any) as any) : a[key],
        newValue: isServiceDate ? (utcDateString(b[key] as any) as any) : b[key],
      }
      out.push(reason)
    }
  })

  // skip if both values are undefined
  out = out.filter((v) => v.oldValue || v.newValue)

  const dateFields = out.filter((v) => v.fieldName.indexOf('Date'))

  //TODO configurable filters
  if (dateFields.length) {
    dateFields.forEach((v) => {
      if (dateEquals(v.oldValue, v.newValue)) {
        out.splice(out.indexOf(v), 1)
      }
    })
  }

  const arrayFields = out.filter((v) => Array.isArray(v.oldValue) || Array.isArray(v.newValue))

  const isEmpty = (a: any[]) => !a || a.length == 0
  if (arrayFields.length) {
    arrayFields.forEach((v) => {
      if (isEmpty(v.oldValue) && isEmpty(v.newValue)) {
        out.splice(out.indexOf(v), 1)
      }
    })
  }

  const linkFields = out.filter((v) => v?.fieldName === 'links')
  if (linkFields.length) {
    linkFields.forEach((v) => {
      v.fieldName = 'diagnosisList'
      v.oldValue = a['diagnosisList']
      v.newValue = b['diagnosisList']
    })
  }

  const modifierFields = out.filter((v) => v?.fieldName === 'modifier')
  if (modifierFields.length) {
    modifierFields.forEach((v) => {
      v.fieldName = 'modifiers'
      v.oldValue = a['modifiers']
      v.newValue = b['modifiers']
    })
  }

  let providerFields = out.filter((v) => v?.fieldName === 'provider')
  if (providerFields.length) {
    providerFields.forEach((v) => {
      if (!isEmpty(v.oldValue) && !isEmpty(v.newValue)) {
        if (v.oldValue.providerId === v.newValue.providerId) {
          out.splice(out.indexOf(v), 1)
        }
      }
    })
  }
  providerFields = out.filter((v) => v?.fieldName === 'secondaryProvider')
  if (providerFields.length) {
    providerFields.forEach((v) => {
      if (!isEmpty(v.oldValue) && !isEmpty(v.newValue)) {
        if (v.oldValue.providerId === v.newValue.providerId) {
          out.splice(out.indexOf(v), 1)
        }
      }
    })
  }

  providerFields = out.filter((v) => v?.fieldName === 'tertiaryProvider')
  if (providerFields.length) {
    providerFields.forEach((v) => {
      if (!isEmpty(v.oldValue) && !isEmpty(v.newValue)) {
        if (v.oldValue.providerId === v.newValue.providerId) {
          out.splice(out.indexOf(v), 1)
        }
      }
    })
  }

  const info: NdcInformation = result['ndcInformation']
  if (info) {
    const aInfo = (a as any).ndcInformation
    const bInfo = (b as any).ndcInformation
    const result: object = updatedDiff(aInfo, bInfo)

    Object.keys(result).forEach((key) => {
      const reason: ChangeReason = {
        fieldName: key,
        oldValue: aInfo[key],
        newValue: bInfo[key],
      }
      out.push(reason)
    })
  } else if (b['ndcInformation']) {
    const bInfo = b['ndcInformation']
    const result: object = updatedDiff(
      {
        ndcDescription: undefined,
        ndcNumber: undefined,
        ndcMeasure: undefined,
        ndcQuantity: undefined,
      },
      bInfo,
    )

    Object.keys(result).forEach((key) => {
      const reason: ChangeReason = {
        fieldName: key,
        oldValue: undefined,
        newValue: bInfo[key],
      }
      out.push(reason)
    })
  }

  const map = out.reduce((accumulator, target) => ({ ...accumulator, [target.fieldName]: target }), {})

  out = Object.values(map)
  console.info('diff vs map', result, out)
  return out
}

function ChangeControl(v: ChangeReason, stringify: Stringify, c?: Control) {
  const valueLabel = stringify[v.fieldName] || ((v: any) => JSON.stringify(v))
  const valueStyle = { textAlign: 'right', color: gray }

  return (
    <Grid
      item
      container
      key={v.fieldName}
      spacing={0}
      direction="row"
      alignItems="flex-end"
      justify="center"
      style={{ width: '100%', flexWrap: 'nowrap', padding: 0 }}
    >
      <Grid item xs={3} style={{ overflowX: 'hidden' }}>
        <Typography
          variant="body2"
          style={{
            color: orange,
            width: '100%',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflowX: 'hidden',
            fontSize: 14,
          }}
        >
          {splitCamelCase(v.fieldName)}
        </Typography>
      </Grid>

      {LabelItem('Old Value: ', { maxWidth: 120 }, 2)}
      {LabelItem(valueLabel(v.oldValue), valueStyle)}

      {LabelItem('New Value : ', { maxWidth: 120 }, 2)}
      {LabelItem(valueLabel(v.newValue), valueStyle)}
      {(c && (
        <Grid item style={{ paddingLeft: 24, minWidth: 220, flexGrow: 1 }}>
          {' '}
          <Auto {...c} />{' '}
        </Grid>
      )) || <Grid item style={{ minWidth: 220, flexGrow: 1 }} />}
    </Grid>
  )
}

type AuditFormData = { notes: string; excludeFromAudit?: boolean } & {
  [key: string]: ChangeReason | string | boolean
}

export type ChangeReasons<T = any> = {
  [key in keyof T]: VisitCode[]
}

type ChangeReasonFormProps<T extends object = object> = {
  original: T
  update: T
  existing?: Audit
  fieldNames: string[]
  changeReasons?: ChangeReasons<T>
  stringify?: Stringify<T>
  onSubmit?: (form: AuditFormData) => void
}

export type ChangeReasonFormApi = {
  reasons: ChangeReason[]
  form: FormApi<AuditFormData>
  component: JSX.Element

  userChangeReasons(): ChangeReason[]
}

export function changeReasonFormApi<T extends object = object>(props: ChangeReasonFormProps<T>): ChangeReasonFormApi {
  const { original, update, fieldNames, onSubmit } = props
  const changeReasons = { ...props.changeReasons }
  const existing: Audit = props.existing || (original as any)?.audit

  const values: AuditFormData = {
    notes: existing?.notes,
    excludeFromAudit: existing?.excludeFromAudit,
  }

  const fields = {
    notes: {
      label: 'Comments',
      style: { width: '100%' },
      multiline: true,
      rows: 2,
    },
    excludeFromAudit: {
      label: 'Education Only?',
      useBooleanValue: true,
    },
  }

  //TODO pass diff configuration
  const reasons: ChangeReason[] = diffReasons<Procedure>(original, update, fieldNames as any)

  const stringify: Stringify<Procedure> = stringifyProcedure(props.stringify)

  reasons.forEach((v) => {
    const list = changeReasons[v.fieldName]
    if (list) {
      values[v.fieldName] = null

      fields[v.fieldName] = {
        list,
        listLabel: (v: VisitCode) => v.description,
        label: 'Reason for Changing',
        required: true,
      }
    }
  })

  function validChangeReasons() {
    let valid = true
    reasons.forEach((v: ChangeReason) => {
      if (fields[v.fieldName]) {
        const f: Control = controls[v.fieldName]
        if (f) {
          const c: VisitCode = f.value.getState().value
          valid = valid && !!c
          if (c) {
            v.changeReasonCode = c.code + ''
            v.changeReasonDescription = c.description
          }
        }
      }
    })

    return valid
  }

  function userChangeReasons(): ChangeReason[] {
    return reasons //.filter(v => v.changeReasonCode); // Show non-required changes ?
  }

  const form = formApi<AuditFormData>({
    values,
    fields,
    valid: () => {
      const valid = validChangeReasons()
      // console.info('validChangeReasons', valid, v,  reasons)
      return valid
    },
    onSubmit: (v) => {
      if (onSubmit) {
        onSubmit(v)
      }
    },
  })
  const controls = form.controls

  const requiredChanges = reasons.filter((v) => fields[v.fieldName])
  const nonRequiredChanges = reasons.filter((v) => !fields[v.fieldName])

  const showOther = !!nonRequiredChanges?.length

  return {
    reasons,
    userChangeReasons,
    form,
    component: (
      <Grid
        container
        direction="column"
        spacing={0}
        justify="flex-end"
        style={{ width: '100%', paddingTop: 0, marginTop: 0 }}
      >
        {<ChangeReasonTable reasons={requiredChanges} stringify={stringify} controls={controls} />}
        <br />
        {showOther && <p style={{ fontSize: 14 }}> Other Changes</p>}
        {showOther && <ChangeReasonTable reasons={nonRequiredChanges} stringify={stringify} controls={controls} />}
        <br />
        <Grid item style={{ width: '100%' }}>
          <Text {...controls.notes} />
        </Grid>
      </Grid>
    ),
  }
}

function Value(label: string) {
  return withTooltip(
    label,
    <Typography
      variant="body2"
      style={{
        width: '100%',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
        paddingLeft: 6,
        fontSize: 10,
      }}
    >
      {label}
    </Typography>,
  )
}

function ChangeRow(v: ChangeReason, stringify: Stringify, c?: Control) {
  const valueLabel = stringify[v.fieldName] || ((v: any) => JSON.stringify(v))
  const cellStyle = { paddingTop: 0, paddingBottom: 0, height: 48 }

  return (
    <TableRow key={v.fieldName}>
      <TableCell component={'th' as any} scope="row" style={cellStyle}>
        {splitCamelCase(v.fieldName)}
      </TableCell>
      <TableCell align="left" style={cellStyle}>
        {Value(valueLabel(v.oldValue))}
      </TableCell>
      <TableCell align="left" style={cellStyle}>
        {Value(valueLabel(v.newValue))}
      </TableCell>
      <TableCell align="right" style={cellStyle}>
        {(c && <Auto {...c} />) || <div />}
      </TableCell>
    </TableRow>
  )
}

type ChangeReasonTableProps = {
  reasons: ChangeReason<any>[]
  stringify: Stringify
  controls: object
  tableWidth?: number
}

function ChangeReasonTable({ reasons, controls, stringify }: ChangeReasonTableProps) {
  const w: any = { width: '100%', flexWrap: 'nowrap' }
  const tableWidth = 900
  const fieldWidth = Math.floor(tableWidth * 0.14)
  const reasonWidth = Math.floor(tableWidth * 0.3)
  return (
    <Grid item container direction="column" style={w}>
      <TableContainer
        component={(a: any) =>
          (
            <Grid
              item
              {...a}
              style={{
                width: tableWidth - 24,
                overflowX: 'hidden',
                overflowY: 'hidden',
                flexWrap: 'nowrap',
              }}
            />
          ) as any
        }
      >
        <MTable size="small" style={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: fieldWidth }}>Field</TableCell>
              <TableCell align="left">Old Value</TableCell>
              <TableCell align="left">New Value</TableCell>
              <TableCell align="center" style={{ width: reasonWidth }}>
                {' '}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{reasons.map((v: ChangeReason) => ChangeRow(v, stringify, controls[v.fieldName]))}</TableBody>
        </MTable>
      </TableContainer>
    </Grid>
  )
}
